import { Injectable } from '@angular/core';

export interface TimeZoneTimestamp {
  timestamp: string;
  timeZone: string;
  timeZoneLabel: string;
}

@Injectable({
  providedIn: 'root'
})
export class TimezoneService {
  private readonly monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  buildTimestamp(date: Date = new Date()): TimeZoneTimestamp {
    const timeZone = this.getCurrentTimeZoneId();

    return {
      timestamp: this.formatLocalIsoWithOffset(date),
      timeZone,
      timeZoneLabel: this.getTimeZoneLabel(date, timeZone),
    };
  }

  buildClientTimestamp(date: Date = new Date()): string {
    return this.buildTimestamp(date).timestamp;
  }

  buildDateTimestamp(date: Date = new Date()): string {
    return this.buildTimestamp(date).timestamp;
  }

  formatStoredDateTime(timestamp: string, timeZone?: string | null, timeZoneLabel?: string | null): string | null {
    const match = this.parseTimestamp(timestamp);

    if (!match) {
      return null;
    }

    const { year, month, day, hoursText, minutes, offsetToken } = match;
    const hasStoredZoneContext = Boolean(timeZone || timeZoneLabel || (offsetToken && offsetToken !== 'Z'));
    if (!hasStoredZoneContext) {
      return null;
    }

    const hoursNumber = Number(hoursText);
    const ampm = hoursNumber >= 12 ? 'PM' : 'AM';
    const hours = (hoursNumber % 12 || 12).toString();
    const dateText = `${month}/${day}/${year.toString().slice(-2)}`;
    const zoneLabel = timeZoneLabel || timeZone || (offsetToken === 'Z' ? 'UTC' : `UTC${offsetToken}`);

    return `${dateText}, ${hours}:${minutes} ${ampm} ${zoneLabel}`.trim();
  }

  parseStoredDateToPickerDate(value?: string | Date | null): Date | null {
    const parsed = this.parseTimestamp(value);
    if (!parsed) {
      return null;
    }

    return new Date(parsed.year, parsed.month - 1, parsed.day);
  }

  formatStoredDate(value?: string | Date | null): string | null {
    const parsed = this.parseTimestamp(value);
    if (!parsed) {
      return null;
    }

    return `${this.monthNames[parsed.month - 1]} ${parsed.day.toString().padStart(2, '0')}, ${parsed.year}`;
  }

  isStoredDateOverdue(value?: string | Date | null, isCompletedLike: boolean = false): boolean {
    const parsed = this.parseTimestamp(value);
    if (!parsed || isCompletedLike) {
      return false;
    }

    return this.compareStoredDateToToday(parsed) < 0;
  }

  isStoredDateDueToday(value?: string | Date | null, isCompletedLike: boolean = false): boolean {
    const parsed = this.parseTimestamp(value);
    if (!parsed || isCompletedLike) {
      return false;
    }

    return this.compareStoredDateToToday(parsed) === 0;
  }

  getStoredDaysOverdue(value?: string | Date | null): number {
    const parsed = this.parseTimestamp(value);
    if (!parsed) {
      return 0;
    }

    const daysFromToday = this.compareStoredDateToToday(parsed);
    return daysFromToday < 0 ? Math.abs(daysFromToday) : 0;
  }

  private getCurrentTimeZoneId(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }

  private getTimeZoneLabel(date: Date, timeZone: string): string {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone,
        timeZoneName: 'short',
      }).formatToParts(date);
      const timeZoneName = parts.find((part) => part.type === 'timeZoneName')?.value;
      if (timeZoneName) {
        return timeZoneName;
      }
    } catch {
      // Fall back to the numeric offset label when timezone names are unavailable.
    }

    return this.getOffsetLabel(date);
  }

  private formatLocalIsoWithOffset(date: Date): string {
    const year = date.getFullYear().toString().padStart(4, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const milliseconds = date.getMilliseconds().toString().padStart(3, '0');
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absoluteOffsetMinutes = Math.abs(offsetMinutes);
    const offsetHours = Math.floor(absoluteOffsetMinutes / 60).toString().padStart(2, '0');
    const offsetRemainderMinutes = (absoluteOffsetMinutes % 60).toString().padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${sign}${offsetHours}:${offsetRemainderMinutes}`;
  }

  private getOffsetLabel(date: Date): string {
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absoluteOffsetMinutes = Math.abs(offsetMinutes);
    const offsetHours = Math.floor(absoluteOffsetMinutes / 60).toString().padStart(2, '0');
    const offsetRemainderMinutes = (absoluteOffsetMinutes % 60).toString().padStart(2, '0');

    return `UTC${sign}${offsetHours}:${offsetRemainderMinutes}`;
  }

  private compareStoredDateToToday(parsed: ParsedTimestamp): number {
    const storedDateValue = Date.UTC(parsed.year, parsed.month - 1, parsed.day);
    const currentDate = this.getCurrentDateParts(parsed.offsetToken);
    const currentDateValue = Date.UTC(currentDate.year, currentDate.month - 1, currentDate.day);

    return Math.round((storedDateValue - currentDateValue) / 86400000);
  }

  private getCurrentDateParts(offsetToken?: string): { year: number; month: number; day: number } {
    const offsetMinutes = this.parseOffsetMinutes(offsetToken);
    if (offsetMinutes === null) {
      const localNow = new Date();
      return {
        year: localNow.getFullYear(),
        month: localNow.getMonth() + 1,
        day: localNow.getDate(),
      };
    }

    const target = new Date(Date.now() + offsetMinutes * 60000);
    return {
      year: target.getUTCFullYear(),
      month: target.getUTCMonth() + 1,
      day: target.getUTCDate(),
    };
  }

  private parseTimestamp(value?: string | Date | null): ParsedTimestamp | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return null;
      }

      return {
        year: value.getFullYear(),
        month: value.getMonth() + 1,
        day: value.getDate(),
        hoursText: value.getHours().toString().padStart(2, '0'),
        minutes: value.getMinutes().toString().padStart(2, '0'),
        offsetToken: this.getOffsetToken(-value.getTimezoneOffset()),
      };
    }

    const match = value.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::\d{2}(?:\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})?)?$/
    );
    if (!match) {
      return null;
    }

    return {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
      hoursText: match[4] ?? '00',
      minutes: match[5] ?? '00',
      offsetToken: match[6],
    };
  }

  private parseOffsetMinutes(offsetToken?: string): number | null {
    if (!offsetToken) {
      return null;
    }

    if (offsetToken === 'Z') {
      return 0;
    }

    const match = offsetToken.match(/^([+-])(\d{2}):(\d{2})$/);
    if (!match) {
      return null;
    }

    const sign = match[1] === '+' ? 1 : -1;
    const hours = Number(match[2]);
    const minutes = Number(match[3]);
    return sign * ((hours * 60) + minutes);
  }

  private getOffsetToken(offsetMinutes: number): string {
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absoluteOffsetMinutes = Math.abs(offsetMinutes);
    const offsetHours = Math.floor(absoluteOffsetMinutes / 60).toString().padStart(2, '0');
    const offsetRemainderMinutes = (absoluteOffsetMinutes % 60).toString().padStart(2, '0');
    return `${sign}${offsetHours}:${offsetRemainderMinutes}`;
  }
}

interface ParsedTimestamp {
  year: number;
  month: number;
  day: number;
  hoursText: string;
  minutes: string;
  offsetToken?: string;
}
